import type { TFunction } from "i18next";
import * as XLSX from "xlsx";
import type { Client, ClientStatus, ClientType, ContractModel } from "@/types";

type LabelMap<T extends string> = Record<T, { label: string }>;

export interface ClientExportLabels {
  status: LabelMap<ClientStatus>;
  type: LabelMap<ClientType>;
  contract: LabelMap<ContractModel>;
}

/** Column order (matches the on-screen table); header text is localised via i18n. */
const COLUMN_KEYS = [
  "clientName",
  "email",
  "phone",
  "status",
  "type",
  "niche",
  "contractModel",
  "monthlyFee",
  "currency",
  "startDate",
  "facebook",
  "instagram",
  "tiktok",
  "notes",
] as const;

/** Per-column widths (characters) for a readable sheet. */
const COLUMN_WIDTHS = [24, 26, 16, 14, 16, 16, 20, 14, 8, 14, 30, 30, 30, 36];

export const CLIENTS_EXPORT_SHEET = "Clients";

function formatDate(value: string | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
}

function toNumber(value: number | string | null | undefined): number {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : 0;
}

/** One spreadsheet row (array form), in COLUMN_KEYS order. Empty values stay blank ""; fee stays numeric. */
function clientRow(client: Client, labels: ClientExportLabels): (string | number)[] {
  return [
    client.name,
    client.email ?? "",
    client.phone ?? "",
    labels.status[client.status]?.label ?? client.status,
    labels.type[client.type]?.label ?? client.type,
    client.niche ?? "",
    labels.contract[client.contractModel]?.label ?? client.contractModel,
    toNumber(client.monthlyFee),
    client.currency ?? "DZD",
    formatDate(client.startDate ?? client.createdAt),
    client.facebookUrl ?? "",
    client.instagramUrl ?? "",
    client.tiktokUrl ?? "",
    client.notes ?? "",
  ];
}

export function clientsExportFileName(date: Date = new Date()): string {
  return `fad-agency-clients-${date.toISOString().slice(0, 10)}.xlsx`;
}

/**
 * Build the clients workbook in memory (testable). Receives the already
 * filtered/sorted list from the page, so the export mirrors what the user sees.
 * Headers come from i18n; status/type/contract render as human labels, never raw enums.
 */
export function buildClientsWorkbook(
  clients: Client[],
  t: TFunction,
  labels: ClientExportLabels,
): XLSX.WorkBook {
  const header = COLUMN_KEYS.map((key) => t(`clients.exportColumns.${key}`));
  const aoa: (string | number)[][] = [header, ...clients.map((client) => clientRow(client, labels))];

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet["!cols"] = COLUMN_WIDTHS.map((wch) => ({ wch }));
  // Auto-filter across the header row + data so the user can sort/filter in Excel.
  const lastCol = XLSX.utils.encode_col(COLUMN_KEYS.length - 1);
  worksheet["!autofilter"] = { ref: `A1:${lastCol}${aoa.length}` };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, CLIENTS_EXPORT_SHEET);
  return workbook;
}

/** Build + download the clients workbook (browser/Tauri save). */
export function exportClientsToExcel(clients: Client[], t: TFunction, labels: ClientExportLabels): void {
  const workbook = buildClientsWorkbook(clients, t, labels);
  XLSX.writeFile(workbook, clientsExportFileName());
}
